import React from 'react';
import { ArrowLeft, Zap, Shield, Globe, TrendingUp, Clock, DollarSign, Users, Award, BarChart3 } from 'lucide-react';
import { t } from '../services/i18n';
import { useSEOMetaTags } from './SEOMetaTags';

interface AdvantagesPageProps {
  onBack: () => void;
  onNavigate?: (page: string) => void;
}

export const AdvantagesPage: React.FC<AdvantagesPageProps> = ({ onBack, onNavigate }) => {
  useSEOMetaTags({
    title: 'Why Choose Orcaseotools? SEO Tools Advantages & Benefits | Best SEO Software',
    description: 'Discover the advantages of using Orcaseotools for SEO, AEO, and GEO optimization. Compare why our SEO tools outperform competitors with faster analysis, comprehensive reports, and worldwide support. Best value SEO software.',
    keywords: 'SEO tools advantages, best SEO software benefits, why choose SEO tools, SEO audit tool comparison, professional SEO advantages, SEO software features, AEO tools benefits, GEO optimization advantages, SEO tool advantages, best SEO audit software',
    canonicalUrl: 'https://orcaseotools.com/advantages',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Advantages - Orcaseotools SEO Tools",
      "description": "Discover the advantages and benefits of using Orcaseotools for SEO, AEO, and GEO optimization",
      "url": "https://orcaseotools.com/advantages",
      "inLanguage": "en-US"
    }
  });

  const advantages = [
    {
      icon: Zap,
      title: 'Lightning-Fast Analysis',
      description: 'Get comprehensive SEO audits in minutes, not hours. Our advanced algorithms analyze your website faster than any competitor, saving you valuable time.',
      benefit: 'Save 10+ hours per audit compared to manual analysis'
    },
    {
      icon: Shield,
      title: 'Enterprise-Grade Security',
      description: 'Your data is protected with bank-level encryption. We never share your website data with third parties, ensuring complete privacy and security.',
      benefit: '100% secure and GDPR compliant'
    },
    {
      icon: Globe,
      title: 'Worldwide Coverage',
      description: 'Analyze websites from any country with support for 150+ languages and international SEO standards. Perfect for global businesses targeting worldwide markets.',
      benefit: 'Support for 150+ countries and languages'
    },
    {
      icon: TrendingUp,
      title: 'AI-Powered Insights',
      description: 'Leverage artificial intelligence for deeper SEO analysis. Our AI identifies patterns and opportunities that traditional tools miss, giving you a competitive edge.',
      benefit: 'AI-driven recommendations increase rankings by 40%'
    },
    {
      icon: Clock,
      title: 'Real-Time Monitoring',
      description: 'Track your SEO performance in real-time with instant alerts for ranking changes, technical issues, and optimization opportunities.',
      benefit: 'Get notified within minutes of any changes'
    },
    {
      icon: DollarSign,
      title: 'Cost-Effective Pricing',
      description: 'Get professional-grade SEO tools at a fraction of the cost of enterprise solutions. No hidden fees, transparent pricing, and flexible plans for every budget.',
      benefit: 'Save up to 80% compared to enterprise SEO tools'
    },
    {
      icon: Users,
      title: 'Expert Support Team',
      description: 'Access our team of certified SEO professionals for guidance, strategy consultation, and technical support. Get help when you need it, 24/7.',
      benefit: '24/7 support from SEO experts'
    },
    {
      icon: Award,
      title: 'Industry Recognition',
      description: 'Trusted by thousands of businesses worldwide. Our SEO tools are recommended by leading marketing agencies and used by Fortune 500 companies.',
      benefit: '4.8/5 rating from 10,000+ users'
    },
    {
      icon: BarChart3,
      title: 'Comprehensive Reporting',
      description: 'Generate detailed SEO reports with actionable insights, visual analytics, and professional PDF exports. Perfect for client presentations and stakeholder reports.',
      benefit: 'Export professional reports in seconds'
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a0e27] text-white py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft size={20} />
          {t('common.back') || 'Back'}
        </button>

        <article className="space-y-12">
          <header className="text-center space-y-4">
            <h1 className="text-5xl font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
              Why Choose Orcaseotools? Our SEO Tools Advantages
            </h1>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Discover why thousands of businesses worldwide trust Orcaseotools for their SEO, AEO, and GEO optimization needs. Compare our advantages and see why we're the best SEO software choice.
            </p>
          </header>

          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {advantages.map((advantage, index) => {
              const Icon = advantage.icon;
              return (
                <div
                  key={index}
                  className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 hover:border-emerald-500 transition-all hover:scale-105 space-y-4"
                >
                  <div className="p-3 bg-emerald-500/20 rounded-xl w-fit">
                    <Icon size={24} className="text-emerald-400" />
                  </div>
                  <h2 className="text-xl font-bold text-white">{advantage.title}</h2>
                  <p className="text-slate-300 text-sm leading-relaxed">{advantage.description}</p>
                  <div className="pt-2 border-t border-slate-700">
                    <p className="text-emerald-400 text-xs font-semibold">{advantage.benefit}</p>
                  </div>
                </div>
              );
            })}
          </section>

          <section className="bg-gradient-to-r from-emerald-900/30 to-cyan-900/30 border border-emerald-500/30 rounded-2xl p-8 space-y-6">
            <h2 className="text-3xl font-bold text-center">Orcaseotools vs. Competitors</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="pb-4 text-slate-300 font-bold">Feature</th>
                    <th className="pb-4 text-emerald-400 font-bold">Orcaseotools</th>
                    <th className="pb-4 text-slate-500 font-bold">Competitors</th>
                  </tr>
                </thead>
                <tbody className="space-y-4">
                  <tr className="border-b border-slate-800">
                    <td className="py-4 text-slate-300">Analysis Speed</td>
                    <td className="py-4 text-emerald-400">⚡ Minutes</td>
                    <td className="py-4 text-slate-500">Hours</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-4 text-slate-300">AEO Optimization</td>
                    <td className="py-4 text-emerald-400">✅ Included</td>
                    <td className="py-4 text-slate-500">❌ Not Available</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-4 text-slate-300">GEO Targeting</td>
                    <td className="py-4 text-emerald-400">🌍 150+ Countries</td>
                    <td className="py-4 text-slate-500">Limited</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-4 text-slate-300">AI-Powered Insights</td>
                    <td className="py-4 text-emerald-400">🤖 Advanced AI</td>
                    <td className="py-4 text-slate-500">Basic</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-4 text-slate-300">Pricing</td>
                    <td className="py-4 text-emerald-400">💰 Affordable</td>
                    <td className="py-4 text-slate-500">Expensive</td>
                  </tr>
                  <tr>
                    <td className="py-4 text-slate-300">Support</td>
                    <td className="py-4 text-emerald-400">24/7 Expert Help</td>
                    <td className="py-4 text-slate-500">Business Hours</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 space-y-4">
            <h2 className="text-2xl font-bold">Start Leveraging Our SEO Advantages Today</h2>
            <p className="text-slate-300">
              Join thousands of businesses worldwide who trust Orcaseotools for their SEO needs. Experience the advantages of faster analysis, comprehensive insights, and expert support.
            </p>
            <button 
              onClick={() => onNavigate ? onNavigate('landing') : onBack()}
              className="px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-bold transition-colors mt-4"
            >
              Get Started Now
            </button>
          </section>
        </article>
      </div>
    </div>
  );
};

